import React, { useEffect, useMemo } from "react";
import { useTranslate, useCustom, useCustomMutation } from "@refinedev/core";
import { useTranslation } from "react-i18next";
import { Form, Input, Select, Row, Col, Divider, InputNumber, Button, Spin } from "antd";
import { getCountryOptions } from "../../../utils/countries";

interface IInvoiceSettings {
    id?: number;
    tenantId?: number;
    companyName?: string;
    address?: {
        street?: string;
        city?: string;
        postalCode?: string;
        country?: string;
    };
    siren?: string;
    nafCode?: string;
    vatNumber?: string;
    iban?: string;
    bic?: string;
    defaultVatRate?: number;
    logoUrl?: string;
    brandColors?: {
        primary?: string;
        secondary?: string;
        accent?: string;
    };
    headerConfig?: {
        fr?: string;
        en?: string;
    };
    footerConfig?: {
        fr?: string;
        en?: string;
    };
    vatExemptionNotice?: {
        fr?: string;
        en?: string;
    };
    paymentTermDays?: number;
    defaultLanguage?: string;
}

interface TenantInvoiceSettingsTabProps {
    tenantId?: number;
    isSuperAdmin: boolean;
    active: boolean;
}

export const TenantInvoiceSettingsTab: React.FC<TenantInvoiceSettingsTabProps> = ({
    tenantId,
    isSuperAdmin,
    active,
}) => {
    const translate = useTranslate();
    const { i18n } = useTranslation();
    const countryOptions = useMemo(
        () => getCountryOptions(i18n.language),
        [i18n.language],
    );
    const [invoiceForm] = Form.useForm<IInvoiceSettings>();

    const {
        data: invoiceSettingsData,
        isLoading: invoiceSettingsLoading,
        refetch: refetchInvoiceSettings,
    } = useCustom<IInvoiceSettings>({
        url: "invoice-settings",
        method: "get",
        config: {
            query: isSuperAdmin && tenantId ? { tenantId } : undefined,
        },
        queryOptions: {
            enabled: active && (!isSuperAdmin || !!tenantId),
            retry: false,
        },
    });

    const { mutate: saveInvoiceSettings, isLoading: savingInvoiceSettings } = useCustomMutation();

    useEffect(() => {
        if (invoiceSettingsData) {
            const data = invoiceSettingsData as IInvoiceSettings;
            invoiceForm.setFieldsValue({
                companyName: data.companyName,
                "address.street": data.address?.street,
                "address.city": data.address?.city,
                "address.postalCode": data.address?.postalCode,
                "address.country": data.address?.country,
                siren: data.siren,
                nafCode: data.nafCode,
                vatNumber: data.vatNumber,
                iban: data.iban,
                bic: data.bic,
                defaultVatRate: data.defaultVatRate ?? 20,
                logoUrl: data.logoUrl,
                "brandColors.primary": data.brandColors?.primary,
                "brandColors.secondary": data.brandColors?.secondary,
                "brandColors.accent": data.brandColors?.accent,
                "headerConfig.fr": data.headerConfig?.fr,
                "headerConfig.en": data.headerConfig?.en,
                "footerConfig.fr": data.footerConfig?.fr,
                "footerConfig.en": data.footerConfig?.en,
                "vatExemptionNotice.fr": data.vatExemptionNotice?.fr,
                "vatExemptionNotice.en": data.vatExemptionNotice?.en,
                paymentTermDays: data.paymentTermDays ?? 0,
                defaultLanguage: data.defaultLanguage ?? "fr",
            } as any);
        }
    }, [invoiceSettingsData, invoiceForm]);

    const handleSaveInvoiceSettings = async () => {
        try {
            const values = await invoiceForm.validateFields();

            const payload = {
                companyName: values.companyName,
                address: {
                    street: (values as any)["address.street"],
                    city: (values as any)["address.city"],
                    postalCode: (values as any)["address.postalCode"],
                    country: (values as any)["address.country"],
                },
                siren: values.siren,
                nafCode: values.nafCode,
                vatNumber: values.vatNumber,
                iban: values.iban,
                bic: values.bic,
                defaultVatRate: values.defaultVatRate,
                logoUrl: (values as any).logoUrl || undefined,
                brandColors: (values as any)["brandColors.primary"]
                    ? {
                          primary: (values as any)["brandColors.primary"],
                          secondary: (values as any)["brandColors.secondary"],
                          accent: (values as any)["brandColors.accent"],
                      }
                    : undefined,
                headerConfig:
                    (values as any)["headerConfig.fr"] || (values as any)["headerConfig.en"]
                        ? {
                              fr: (values as any)["headerConfig.fr"] || "",
                              en: (values as any)["headerConfig.en"] || "",
                          }
                        : undefined,
                footerConfig:
                    (values as any)["footerConfig.fr"] || (values as any)["footerConfig.en"]
                        ? {
                              fr: (values as any)["footerConfig.fr"] || "",
                              en: (values as any)["footerConfig.en"] || "",
                          }
                        : undefined,
                vatExemptionNotice:
                    (values as any)["vatExemptionNotice.fr"] || (values as any)["vatExemptionNotice.en"]
                        ? {
                              fr: (values as any)["vatExemptionNotice.fr"] || "",
                              en: (values as any)["vatExemptionNotice.en"] || "",
                          }
                        : undefined,
                paymentTermDays: values.paymentTermDays,
                defaultLanguage: values.defaultLanguage,
            };

            saveInvoiceSettings(
                {
                    url: isSuperAdmin && tenantId ? `invoice-settings?tenantId=${tenantId}` : "invoice-settings",
                    method: "put",
                    values: payload,
                },
                {
                    onSuccess: () => {
                        (window as any).antd?.message?.success?.(
                            translate(
                                "tenants.invoiceSettings.messages.saveSuccess",
                                "Invoice settings saved successfully",
                            ),
                        );
                        refetchInvoiceSettings();
                    },
                    onError: () => {
                        (window as any).antd?.message?.error?.(
                            translate(
                                "tenants.invoiceSettings.messages.saveError",
                                "Failed to save invoice settings",
                            ),
                        );
                    },
                },
            );
        } catch (error) {
            // Validation errors handled by form
        }
    };

    return (
        <Spin spinning={invoiceSettingsLoading || savingInvoiceSettings}>
            <Form form={invoiceForm} layout="vertical">
                <Divider orientation="left">
                    {translate(
                        "tenants.invoiceSettings.sections.companyInfo",
                        "Company Information",
                    )}
                </Divider>

                <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            label={translate(
                                "tenants.invoiceSettings.fields.companyName",
                                "Company Name",
                            )}
                            name="companyName"
                            rules={[
                                {
                                    required: true,
                                    message: translate(
                                        "tenants.invoiceSettings.errors.companyNameRequired",
                                        "Company name is required",
                                    ),
                                },
                            ]}
                        >
                            <Input
                                placeholder={translate(
                                    "tenants.invoiceSettings.placeholders.companyName",
                                    "Enter company legal name",
                                )}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            label={translate(
                                "tenants.invoiceSettings.fields.siren",
                                "SIREN",
                            )}
                            name="siren"
                            rules={[
                                {
                                    required: true,
                                    message: translate(
                                        "tenants.invoiceSettings.errors.sirenRequired",
                                        "SIREN is required",
                                    ),
                                },
                            ]}
                        >
                            <Input
                                placeholder={translate(
                                    "tenants.invoiceSettings.placeholders.siren",
                                    "123456789",
                                )}
                                maxLength={9}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            label={translate(
                                "tenants.invoiceSettings.fields.nafCode",
                                "NAF Code",
                            )}
                            name="nafCode"
                        >
                            <Input
                                placeholder={translate(
                                    "tenants.invoiceSettings.placeholders.nafCode",
                                    "6201Z",
                                )}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            label={translate(
                                "tenants.invoiceSettings.fields.vatNumber",
                                "VAT Number",
                            )}
                            name="vatNumber"
                        >
                            <Input
                                placeholder={translate(
                                    "tenants.invoiceSettings.placeholders.vatNumber",
                                    "FR12345678901",
                                )}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Divider orientation="left">
                    {translate("tenants.invoiceSettings.sections.address", "Address")}
                </Divider>

                <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
                    <Col xs={24}>
                        <Form.Item
                            label={translate(
                                "tenants.invoiceSettings.fields.street",
                                "Street",
                            )}
                            name="address.street"
                            rules={[
                                {
                                    required: true,
                                    message: translate(
                                        "tenants.invoiceSettings.errors.streetRequired",
                                        "Street is required",
                                    ),
                                },
                            ]}
                        >
                            <Input
                                placeholder={translate(
                                    "tenants.invoiceSettings.placeholders.street",
                                    "123 Rue de la Paix",
                                )}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
                    <Col xs={24} sm={8}>
                        <Form.Item
                            label={translate(
                                "tenants.invoiceSettings.fields.postalCode",
                                "Postal Code",
                            )}
                            name="address.postalCode"
                            rules={[
                                {
                                    required: true,
                                    message: translate(
                                        "tenants.invoiceSettings.errors.postalCodeRequired",
                                        "Postal code is required",
                                    ),
                                },
                            ]}
                        >
                            <Input
                                placeholder={translate(
                                    "tenants.invoiceSettings.placeholders.postalCode",
                                    "75001",
                                )}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={8}>
                        <Form.Item
                            label={translate(
                                "tenants.invoiceSettings.fields.city",
                                "City",
                            )}
                            name="address.city"
                            rules={[
                                {
                                    required: true,
                                    message: translate(
                                        "tenants.invoiceSettings.errors.cityRequired",
                                        "City is required",
                                    ),
                                },
                            ]}
                        >
                            <Input
                                placeholder={translate(
                                    "tenants.invoiceSettings.placeholders.city",
                                    "Paris",
                                )}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={8}>
                        <Form.Item
                            label={translate(
                                "tenants.invoiceSettings.fields.country",
                                "Country",
                            )}
                            name="address.country"
                            rules={[
                                {
                                    required: true,
                                    message: translate(
                                        "tenants.invoiceSettings.errors.countryRequired",
                                        "Country is required",
                                    ),
                                },
                            ]}
                        >
                            <Select
                                showSearch
                                optionFilterProp="label"
                                allowClear
                                options={countryOptions}
                                placeholder={translate(
                                    "tenants.invoiceSettings.placeholders.country",
                                    "France",
                                )}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Divider orientation="left">
                    {translate(
                        "tenants.invoiceSettings.sections.bankingInfo",
                        "Banking Information",
                    )}
                </Divider>

                <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
                    <Col xs={24} sm={16}>
                        <Form.Item
                            label={translate(
                                "tenants.invoiceSettings.fields.iban",
                                "IBAN",
                            )}
                            name="iban"
                        >
                            <Input
                                placeholder={translate(
                                    "tenants.invoiceSettings.placeholders.iban",
                                    "FR7630001007941234567890185",
                                )}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={8}>
                        <Form.Item
                            label={translate("tenants.invoiceSettings.fields.bic", "BIC")}
                            name="bic"
                        >
                            <Input
                                placeholder={translate(
                                    "tenants.invoiceSettings.placeholders.bic",
                                    "BNPAFRPP",
                                )}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Divider orientation="left">
                    {translate(
                        "tenants.invoiceSettings.sections.invoiceDefaults",
                        "Invoice Defaults",
                    )}
                </Divider>

                <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
                    <Col xs={24} sm={8}>
                        <Form.Item
                            label={translate(
                                "tenants.invoiceSettings.fields.defaultVatRate",
                                "Default VAT Rate (%)",
                            )}
                            name="defaultVatRate"
                            initialValue={20}
                        >
                            <InputNumber min={0} max={100} className="w-full" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={8}>
                        <Form.Item
                            label={translate(
                                "tenants.invoiceSettings.fields.paymentTermDays",
                                "Payment Term (days)",
                            )}
                            name="paymentTermDays"
                            initialValue={0}
                            tooltip={translate(
                                "tenants.invoiceSettings.tooltips.paymentTermDays",
                                "0 = immediate payment",
                            )}
                        >
                            <InputNumber min={0} max={365} className="w-full" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={8}>
                        <Form.Item
                            label={translate(
                                "tenants.invoiceSettings.fields.defaultLanguage",
                                "Default Language",
                            )}
                            name="defaultLanguage"
                            initialValue="fr"
                        >
                            <Select
                                options={[
                                    { label: "Français", value: "fr" },
                                    { label: "English", value: "en" },
                                ]}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Divider orientation="left">
                    {translate(
                        "tenants.invoiceSettings.sections.branding",
                        "Branding",
                    )}
                </Divider>

                <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
                    <Col xs={24}>
                        <Form.Item
                            label={translate(
                                "tenants.invoiceSettings.fields.logoUrl",
                                "Logo URL",
                            )}
                            name="logoUrl"
                        >
                            <Input
                                placeholder={translate(
                                    "tenants.invoiceSettings.placeholders.logoUrl",
                                    "https://example.com/logo.png",
                                )}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
                    <Col xs={24} sm={8}>
                        <Form.Item
                            label={translate(
                                "tenants.invoiceSettings.fields.primaryColor",
                                "Primary Color",
                            )}
                            name="brandColors.primary"
                        >
                            <Input placeholder="#1a365d" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={8}>
                        <Form.Item
                            label={translate(
                                "tenants.invoiceSettings.fields.secondaryColor",
                                "Secondary Color",
                            )}
                            name="brandColors.secondary"
                        >
                            <Input placeholder="#2d3748" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={8}>
                        <Form.Item
                            label={translate(
                                "tenants.invoiceSettings.fields.accentColor",
                                "Accent Color",
                            )}
                            name="brandColors.accent"
                        >
                            <Input placeholder="#3182ce" />
                        </Form.Item>
                    </Col>
                </Row>

                <Divider orientation="left">
                    {translate(
                        "tenants.invoiceSettings.sections.customText",
                        "Custom Header & Footer",
                    )}
                </Divider>

                <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            label={translate(
                                "tenants.invoiceSettings.fields.headerFr",
                                "Header (French)",
                            )}
                            name="headerConfig.fr"
                        >
                            <Input.TextArea
                                rows={2}
                                placeholder={translate(
                                    "tenants.invoiceSettings.placeholders.headerFr",
                                    "Custom header text in French",
                                )}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            label={translate(
                                "tenants.invoiceSettings.fields.headerEn",
                                "Header (English)",
                            )}
                            name="headerConfig.en"
                        >
                            <Input.TextArea
                                rows={2}
                                placeholder={translate(
                                    "tenants.invoiceSettings.placeholders.headerEn",
                                    "Custom header text in English",
                                )}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            label={translate(
                                "tenants.invoiceSettings.fields.footerFr",
                                "Footer (French)",
                            )}
                            name="footerConfig.fr"
                        >
                            <Input.TextArea
                                rows={2}
                                placeholder={translate(
                                    "tenants.invoiceSettings.placeholders.footerFr",
                                    "Custom footer text in French",
                                )}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            label={translate(
                                "tenants.invoiceSettings.fields.footerEn",
                                "Footer (English)",
                            )}
                            name="footerConfig.en"
                        >
                            <Input.TextArea
                                rows={2}
                                placeholder={translate(
                                    "tenants.invoiceSettings.placeholders.footerEn",
                                    "Custom footer text in English",
                                )}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Divider orientation="left">
                    {translate(
                        "tenants.invoiceSettings.sections.vatExemption",
                        "VAT Exemption Notice",
                    )}
                </Divider>

                <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            label={translate(
                                "tenants.invoiceSettings.fields.vatExemptionNoticeFr",
                                "VAT Exemption Notice (French)",
                            )}
                            name="vatExemptionNotice.fr"
                        >
                            <Input.TextArea
                                rows={2}
                                placeholder={translate(
                                    "tenants.invoiceSettings.placeholders.vatExemptionNoticeFr",
                                    "TVA non applicable, art. 293 B du CGI",
                                )}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            label={translate(
                                "tenants.invoiceSettings.fields.vatExemptionNoticeEn",
                                "VAT Exemption Notice (English)",
                            )}
                            name="vatExemptionNotice.en"
                        >
                            <Input.TextArea
                                rows={2}
                                placeholder={translate(
                                    "tenants.invoiceSettings.placeholders.vatExemptionNoticeEn",
                                    "VAT not applicable under Article 293B of the French Tax Code",
                                )}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={{ xs: 8, sm: 16, md: 24 }} className="mt-4">
                    <Col xs={24}>
                        <Button
                            type="primary"
                            onClick={handleSaveInvoiceSettings}
                            loading={savingInvoiceSettings}
                        >
                            {translate("buttons.save", "Save")}
                        </Button>
                    </Col>
                </Row>
            </Form>
        </Spin>
    );
};

